"use client";

import { css, cx } from "styled-system/css";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useToast } from "@/hooks/useToast";
import { cf } from "@/components/brand/sfturnos-styles";

type AgendaOption = { id: string; name: string; color: string | null };

type ServiceOption = { id: string; name: string };

type CalendarEntityType = "professional" | "service" | "custom";

type CalendarMapping = { entityType: "service" | "custom"; serviceId?: string; customLabel?: string };

type GoogleCalendarInfo = {
  id: string;
  summary: string;
  backgroundColor: string | null;
  primary: boolean;
  accessRole: string;
  timeZone: string | null;
  utcOffsetMinutes: number | null;
};

function formatUtcOffset(minutes: number): string {
  const sign = minutes <= 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

type StaffRow = {
  id: string;
  name: string;
  googleColorId: string;
  isActive: boolean;
  googleCalendarId: string | null;
  googleCalendarName: string | null;
  googleCalendarBgColor: string | null;
};

export function DashboardGoogleCalendar() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const linkedToastShown = useRef(false);

  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleSyncEnabled, setGoogleSyncEnabled] = useState(true);
  const [googleBlockEnabled, setGoogleBlockEnabled] = useState(false);
  const [googleConnectPreset, setGoogleConnectPreset] = useState<"platform_only" | "platform_and_google_busy">(
    "platform_only",
  );
  const [googleLastSyncAt, setGoogleLastSyncAt] = useState<string | null>(null);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [googleSaving, setGoogleSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Multi-agenda SFTurnos: otras agendas del usuario
  const [otherAgendas, setOtherAgendas] = useState<AgendaOption[]>([]);
  const [selectedAgendas, setSelectedAgendas] = useState<Set<string>>(new Set());

  // Calendarios de Google detectados + staff
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarInfo[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [calendarMappings, setCalendarMappings] = useState<Record<string, CalendarMapping>>({});
  const [customLabelDrafts, setCustomLabelDrafts] = useState<Record<string, string>>({});
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [professionalTimezone, setProfessionalTimezone] = useState<string | null>(null);
  const [professionalUtcOffsetMinutes, setProfessionalUtcOffsetMinutes] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [rGoogle, rAgendas] = await Promise.all([
        fetchWithSessionSync("/api/integrations/google/me", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/agendas", { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (rGoogle.ok) {
        const g = (await rGoogle.json()) as {
          connected: boolean;
          syncEnabled: boolean;
          blockEnabled: boolean;
          lastSyncAt: string | null;
          selectedCalendarIds: string[];
        };
        setGoogleConnected(g.connected);
        setGoogleSyncEnabled(g.syncEnabled);
        setGoogleBlockEnabled(g.blockEnabled);
        setGoogleLastSyncAt(g.lastSyncAt);
        setSelectedCalendarIds(g.selectedCalendarIds ?? []);

        if (g.connected) {
          void loadCalendars();
        }
      }
      if (rAgendas.ok) {
        const data = (await rAgendas.json()) as {
          agendas: AgendaOption[];
          activeProfessionalId: string | null;
        };
        const others = data.agendas.filter((a) => a.id !== data.activeProfessionalId);
        setOtherAgendas(others);
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // Mostrar toast de auto-vinculación al redirigir desde OAuth
  useEffect(() => {
    if (!linkedToastShown.current && searchParams.get("linked") === "1") {
      linkedToastShown.current = true;
      // Esperar a que los calendarios carguen para mostrar el toast con el conteo
      setTimeout(() => {
        toast.success("Google Calendar conectado. Revisá los vínculos con tus profesionales abajo.");
      }, 600);
    }
  }, [searchParams]);

  async function loadCalendars() {
    setCalendarsLoading(true);
    const r = await fetchWithSessionSync("/api/integrations/google/calendars", { credentials: "include" }, refresh);
    setCalendarsLoading(false);
    if (!r.ok) return;
    const d = (await r.json()) as {
      calendars: GoogleCalendarInfo[];
      staff: StaffRow[];
      services?: ServiceOption[];
      calendarMappings?: Record<string, CalendarMapping>;
      professionalTimezone?: string;
      professionalUtcOffsetMinutes?: number;
    };
    setGoogleCalendars(d.calendars);
    setStaff(d.staff);
    setServices(d.services ?? []);
    setCalendarMappings(d.calendarMappings ?? {});
    if (d.professionalTimezone) setProfessionalTimezone(d.professionalTimezone);
    if (typeof d.professionalUtcOffsetMinutes === "number") setProfessionalUtcOffsetMinutes(d.professionalUtcOffsetMinutes);
  }

  async function refreshCalendars() {
    setCalendarsLoading(true);
    const r = await fetchWithSessionSync(
      "/api/integrations/google/refresh-calendars",
      { method: "POST", credentials: "include" },
      refresh,
    );
    setCalendarsLoading(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(j.error ?? "No se pudo actualizar la lista de agendas.");
      return;
    }
    const d = (await r.json()) as {
      calendars: GoogleCalendarInfo[];
      staff: StaffRow[];
      services?: ServiceOption[];
      calendarMappings?: Record<string, CalendarMapping>;
      professionalTimezone?: string;
      professionalUtcOffsetMinutes?: number;
    };
    setGoogleCalendars(d.calendars);
    setStaff(d.staff);
    setServices(d.services ?? []);
    setCalendarMappings(d.calendarMappings ?? {});
    if (d.professionalTimezone) setProfessionalTimezone(d.professionalTimezone);
    if (typeof d.professionalUtcOffsetMinutes === "number") setProfessionalUtcOffsetMinutes(d.professionalUtcOffsetMinutes);
    toast.success("Agendas de Google actualizadas");
  }

  async function importStaff() {
    setCalendarsLoading(true);
    const r = await fetchWithSessionSync(
      "/api/integrations/google/import-staff",
      { method: "POST", credentials: "include" },
      refresh,
    );
    setCalendarsLoading(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(j.error ?? "No se pudo importar los profesionales.");
      return;
    }
    const d = (await r.json()) as { created: number; updated: number; staff: StaffRow[] };
    setStaff(d.staff);
    const msg = d.created > 0
      ? `${d.created} profesional${d.created > 1 ? "es" : ""} importado${d.created > 1 ? "s" : ""} desde Google`
      : "Profesionales ya actualizados";
    toast.success(msg);
  }

  async function updateGoogleConfig(next: Partial<{ syncEnabled: boolean; blockEnabled: boolean }>) {
    setGoogleSaving(true);
    const r = await fetchWithSessionSync(
      "/api/integrations/google/config",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      },
      refresh,
    );
    setGoogleSaving(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(j.error ?? "No se pudo actualizar Google Calendar.");
      return;
    }
    const g = (await r.json()) as { syncEnabled: boolean; blockEnabled: boolean };
    setGoogleSyncEnabled(g.syncEnabled);
    setGoogleBlockEnabled(g.blockEnabled);
    toast.success("Guardado");
  }

  async function toggleSelectedCalendar(calId: string, checked: boolean) {
    const next = checked
      ? [...selectedCalendarIds.filter((x) => x !== calId), calId]
      : selectedCalendarIds.filter((x) => x !== calId);
    setSelectedCalendarIds(next);
    const r = await fetchWithSessionSync(
      "/api/integrations/google/selected-calendars",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calendarIds: next }),
      },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo guardar la selección.");
      setSelectedCalendarIds(selectedCalendarIds);
    }
  }

  async function linkStaffCalendar(staffId: string, calId: string | null) {
    const cal = googleCalendars.find((c) => c.id === calId);
    const r = await fetchWithSessionSync(
      `/api/dashboard/agenda-staff/${staffId}/google-calendar`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          googleCalendarId: calId,
          googleCalendarName: cal?.summary ?? null,
          googleCalendarBgColor: cal?.backgroundColor ?? null,
        }),
      },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo guardar el vínculo.");
      return;
    }
    setStaff((prev) =>
      prev.map((s) =>
        s.id === staffId
          ? { ...s, googleCalendarId: calId, googleCalendarName: cal?.summary ?? null, googleCalendarBgColor: cal?.backgroundColor ?? null }
          : s,
      ),
    );
  }

  function entityTypeFor(calId: string): CalendarEntityType {
    return calendarMappings[calId]?.entityType ?? "professional";
  }

  async function saveCalendarMapping(
    calId: string,
    entityType: CalendarEntityType,
    extra: { serviceId?: string | null; customLabel?: string | null } = {},
  ) {
    const r = await fetchWithSessionSync(
      "/api/integrations/google/calendar-mapping",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calendarId: calId, entityType, ...extra }),
      },
      refresh,
    );
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(j.error ?? "No se pudo guardar la agenda.");
      return false;
    }
    setCalendarMappings((prev) => {
      const next = { ...prev };
      if (entityType === "professional") {
        delete next[calId];
      } else if (entityType === "service") {
        next[calId] = { entityType: "service", serviceId: extra.serviceId ?? undefined };
      } else {
        next[calId] = { entityType: "custom", customLabel: extra.customLabel ?? undefined };
      }
      return next;
    });
    if (entityType !== "professional") {
      // El backend desvincula cualquier profesional que tuviera esta agenda asignada.
      setStaff((prev) =>
        prev.map((s) => (s.googleCalendarId === calId ? { ...s, googleCalendarId: null, googleCalendarName: null, googleCalendarBgColor: null } : s)),
      );
    }
    return true;
  }

  async function changeEntityType(calId: string, entityType: CalendarEntityType) {
    if (entityType === "professional") {
      await saveCalendarMapping(calId, "professional");
      return;
    }
    if (entityType === "service") {
      const firstService = services[0]?.id;
      if (!firstService) {
        toast.error("No hay servicios cargados para vincular.");
        return;
      }
      await saveCalendarMapping(calId, "service", { serviceId: firstService });
      return;
    }
    // "custom": no guardamos hasta que haya una etiqueta; solo cambiamos la vista localmente.
    setCalendarMappings((prev) => ({ ...prev, [calId]: { entityType: "custom", customLabel: customLabelDrafts[calId] ?? "" } }));
  }

  if (!loaded) {
    return (
      <DashboardPageShell eyebrow="Configuración" title="Google Calendar">
        <LoadingFrame variant="light" minHeight={240} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Configuración"
      title="Google Calendar"
      description="Conectá y gestioná la sincronización de tus turnos con Google Calendar."
    >
      <section className={cx(cf.dashSurface, css({ p: "5", maxW: "42rem" }))}>
        <div className={css({ display: "flex", alignItems: "center", gap: "2", mb: "3" })}>
          <img src="/favicon-32x32.png" alt="SFTurnos" className={css({ w: "6", h: "6", rounded: "sm" })} />
          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>Integración SFTurnos + Google Calendar</p>
        </div>

        {!googleConnected ? (
          <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
            <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6" })}>
              Elegí cómo querés usar la integración y después tocá conectar. Podés cambiarlo después en esta misma pantalla.
            </p>
            <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
              <label
                className={css({
                  display: "flex",
                  gap: "3",
                  p: "3",
                  rounded: "lg",
                  cursor: "pointer",
                  borderWidth: "2px",
                  borderStyle: "solid",
                  borderColor: googleConnectPreset === "platform_only" ? "token(colors.brand.500)" : "#e2e8f0",
                  bg: googleConnectPreset === "platform_only" ? "rgba(99, 102, 241, 0.06)" : "white",
                })}
              >
                <input
                  type="radio"
                  name="google-connect-preset"
                  checked={googleConnectPreset === "platform_only"}
                  onChange={() => setGoogleConnectPreset("platform_only")}
                />
                <span>
                  <span className={css({ display: "block", fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                    Solo turnos en SFTurnos + copiar esos turnos a Google
                  </span>
                  <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "1", lineHeight: "1.5" })}>
                    En el calendario del panel ves únicamente tus reservas de la plataforma. Esos mismos turnos se crean o actualizan en tu Google Calendar.
                  </span>
                </span>
              </label>
              <label
                className={css({
                  display: "flex",
                  gap: "3",
                  p: "3",
                  rounded: "lg",
                  cursor: "pointer",
                  borderWidth: "2px",
                  borderStyle: "solid",
                  borderColor: googleConnectPreset === "platform_and_google_busy" ? "token(colors.brand.500)" : "#e2e8f0",
                  bg: googleConnectPreset === "platform_and_google_busy" ? "rgba(99, 102, 241, 0.06)" : "white",
                })}
              >
                <input
                  type="radio"
                  name="google-connect-preset"
                  checked={googleConnectPreset === "platform_and_google_busy"}
                  onChange={() => setGoogleConnectPreset("platform_and_google_busy")}
                />
                <span>
                  <span className={css({ display: "block", fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                    Turnos en SFTurnos + tu ocupación de Google
                  </span>
                  <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "1", lineHeight: "1.5" })}>
                    Además de lo anterior, importamos de Google lo que tengas agendado (como franjas ocupadas) para verlo en SFTurnos y coordinar reservas sin solapamientos.
                  </span>
                </span>
              </label>
            </div>
            {otherAgendas.length > 0 && (
              <div className={css({ p: "3", rounded: "lg", bg: "#f8fafc", borderWidth: "1px", borderColor: "#e2e8f0" })}>
                <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "2" })}>
                  Compartir también con otras agendas
                </p>
                <p className={css({ fontSize: "xs", color: "#64748b", mb: "2", lineHeight: "1.5" })}>
                  Seleccioná las agendas que usarán la misma cuenta de Google. Cada una puede tener su propia configuración después de conectar.
                </p>
                <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                  {otherAgendas.map((ag) => (
                    <label key={ag.id} className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155", cursor: "pointer" })}>
                      <input
                        type="checkbox"
                        checked={selectedAgendas.has(ag.id)}
                        onChange={(e) => {
                          const next = new Set(selectedAgendas);
                          if (e.target.checked) next.add(ag.id);
                          else next.delete(ag.id);
                          setSelectedAgendas(next);
                        }}
                      />
                      <span
                        className={css({ w: "3", h: "3", rounded: "full", flexShrink: 0 })}
                        style={{ backgroundColor: ag.color ?? "#6366f1" }}
                      />
                      {ag.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <a
              href={`/api/integrations/google/auth?preset=${googleConnectPreset}${selectedAgendas.size > 0 ? `&applyToAgendas=${[...selectedAgendas].join(",")}` : ""}`}
              className={css({
                px: "4",
                py: "2.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                textDecoration: "none",
                background: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                textAlign: "center",
              })}
            >
              Conectar con Google
            </a>
          </div>
        ) : (
          <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
            <div className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "center" })}>
              <span
                className={css({
                  display: "inline-block",
                  px: "3",
                  py: "1.5",
                  rounded: "999px",
                  fontSize: "xs",
                  fontWeight: "semibold",
                  color: "#166534",
                  background: "#ecfdf5",
                  borderWidth: "1px",
                  borderColor: "#bbf7d0",
                })}
              >
                Conectado
              </span>
              {googleLastSyncAt ? (
                <span className={css({ fontSize: "xs", color: "#64748b" })}>
                  Último sync: {new Date(googleLastSyncAt).toLocaleString("es-AR")}
                </span>
              ) : null}
            </div>

            <label className={css({ display: "flex", alignItems: "flex-start", gap: "2", fontSize: "sm", color: "#334155" })}>
              <input
                type="checkbox"
                className={css({ mt: "0.5" })}
                checked={googleSyncEnabled}
                disabled={googleSaving}
                onChange={(e) => void updateGoogleConfig({ syncEnabled: e.target.checked })}
              />
              <span>
                <span className={css({ fontWeight: "medium" })}>Enviar mis turnos de SFTurnos a Google Calendar</span>
                <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                  Crea o actualiza eventos en el calendario de Google con cada reserva de la plataforma.
                </span>
              </span>
            </label>

            <label className={css({ display: "flex", alignItems: "flex-start", gap: "2", fontSize: "sm", color: "#334155" })}>
              <input
                type="checkbox"
                className={css({ mt: "0.5" })}
                checked={googleBlockEnabled}
                disabled={googleSaving}
                onChange={(e) => void updateGoogleConfig({ blockEnabled: e.target.checked })}
              />
              <span>
                <span className={css({ fontWeight: "medium" })}>Mostrar en SFTurnos la ocupación de mi Google Calendar</span>
                <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                  Importa eventos externos como franjas ocupadas en el calendario del panel (no son turnos de la plataforma) para evitar choques al reservar.
                </span>
              </span>
            </label>

            <div className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "center" })}>
              <button
                type="button"
                disabled={googleSaving}
                onClick={async () => {
                  setGoogleSaving(true);
                  const r = await fetchWithSessionSync("/api/integrations/google/sync", { method: "POST", credentials: "include" }, refresh);
                  setGoogleSaving(false);
                  const j = (await r.json().catch(() => ({}))) as { error?: string; reconnect?: boolean };
                  if (!r.ok) {
                    if (j.reconnect) {
                      setGoogleConnected(false);
                      toast.error(j.error ?? "La conexión con Google expiró. Volvé a conectar.");
                    } else {
                      toast.error(j.error ?? "No se pudo sincronizar.");
                    }
                    return;
                  }
                  const rr = await fetchWithSessionSync("/api/integrations/google/me", { credentials: "include" }, refresh);
                  if (rr.ok) {
                    const g = (await rr.json()) as {
                      lastSyncAt: string | null;
                      syncEnabled: boolean;
                      blockEnabled: boolean;
                      connected: boolean;
                      selectedCalendarIds: string[];
                    };
                    setGoogleLastSyncAt(g.lastSyncAt);
                    setGoogleSyncEnabled(g.syncEnabled);
                    setGoogleBlockEnabled(g.blockEnabled);
                    setGoogleConnected(g.connected);
                    setSelectedCalendarIds(g.selectedCalendarIds ?? []);
                  }
                  toast.success("Sincronización lista");
                }}
                className={css({
                  px: "4",
                  py: "2.5",
                  rounded: "lg",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "white",
                  border: "none",
                  cursor: googleSaving ? "wait" : "pointer",
                  background: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                  opacity: googleSaving ? 0.8 : 1,
                })}
              >
                {googleSaving ? "Sincronizando…" : "Sincronizar ahora"}
              </button>

              <button
                type="button"
                disabled={googleSaving}
                onClick={async () => {
                  setGoogleSaving(true);
                  const r = await fetchWithSessionSync("/api/integrations/google/disconnect", { method: "DELETE" }, refresh);
                  setGoogleSaving(false);
                  if (!r.ok) {
                    const j = (await r.json().catch(() => ({}))) as { error?: string };
                    toast.error(j.error ?? "No se pudo desconectar.");
                    return;
                  }
                  window.location.reload();
                }}
                className={css({
                  px: "4",
                  py: "2.5",
                  rounded: "lg",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "#334155",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  borderStyle: "solid",
                  cursor: googleSaving ? "wait" : "pointer",
                  background: "#f8fafc",
                  opacity: googleSaving ? 0.85 : 1,
                })}
              >
                Desconectar
              </button>
            </div>

            {/* Sección: agendas de Google detectadas */}
            <div className={css({ mt: "2", borderTopWidth: "1px", borderColor: "#e2e8f0", pt: "4" })}>
              <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "3", flexWrap: "wrap", gap: "2" })}>
                <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                  Agendas de Google detectadas
                </p>
                <button
                  type="button"
                  disabled={calendarsLoading}
                  onClick={() => void refreshCalendars()}
                  className={css({
                    px: "3",
                    py: "1.5",
                    rounded: "md",
                    fontSize: "xs",
                    fontWeight: "medium",
                    color: "#4f46e5",
                    borderWidth: "1px",
                    borderColor: "#c7d2fe",
                    borderStyle: "solid",
                    bg: "#eef2ff",
                    cursor: calendarsLoading ? "wait" : "pointer",
                    opacity: calendarsLoading ? 0.7 : 1,
                  })}
                >
                  {calendarsLoading ? "Actualizando…" : "Actualizar agendas de Google"}
                </button>
              </div>

              {calendarsLoading && googleCalendars.length === 0 ? (
                <LoadingFrame variant="light" minHeight={80} />
              ) : googleCalendars.length === 0 ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>
                  No se detectaron agendas. Tocá "Actualizar agendas de Google" para buscarlas.
                </p>
              ) : (
                <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                  <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>
                    Seleccioná qué agendas importar como ocupación y vinculalas con un profesional de tu equipo.
                  </p>
                  {googleCalendars.map((cal) => {
                    const linkedStaff = staff.find((s) => s.googleCalendarId === cal.id);
                    const isSelected = selectedCalendarIds.includes(cal.id);
                    // Comparamos por offset UTC real, no por nombre de tz IANA: dos zonas distintas
                    // (ej. America/Argentina/Cordoba vs. America/Argentina/Buenos_Aires) pueden compartir
                    // el mismo offset real y no generan ningún desfase de hora.
                    const tzMismatch = Boolean(
                      professionalUtcOffsetMinutes != null &&
                        cal.utcOffsetMinutes != null &&
                        cal.utcOffsetMinutes !== professionalUtcOffsetMinutes,
                    );
                    return (
                      <div
                        key={cal.id}
                        className={css({
                          display: "flex",
                          flexDir: "column",
                          gap: "2",
                          p: "3",
                          rounded: "lg",
                          bg: "#f8fafc",
                          borderWidth: "1px",
                          borderColor: tzMismatch ? "#fcd34d" : "#e2e8f0",
                        })}
                      >
                      <div
                        className={css({
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "3",
                        })}
                      >
                        {/* Color swatch + nombre */}
                        <div className={css({ display: "flex", alignItems: "center", gap: "2", flex: "1", minW: "0" })}>
                          <span
                            className={css({ w: "3.5", h: "3.5", rounded: "full", flexShrink: 0, borderWidth: "1px", borderColor: "rgba(0,0,0,0.1)" })}
                            style={{ backgroundColor: cal.backgroundColor ?? "#aaa" }}
                          />
                          <span className={css({ fontSize: "sm", color: "#0f172a", fontWeight: cal.primary ? "semibold" : "normal", truncate: true })}>
                            {cal.summary}
                            {cal.primary && <span className={css({ ml: "1", fontSize: "xs", color: "#64748b", fontWeight: "normal" })}>(principal)</span>}
                          </span>
                        </div>

                        {/* Qué representa esta agenda */}
                        <select
                          className={css({ px: "2", py: "1.5", rounded: "md", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "xs", color: "#334155", bg: "white", flexShrink: 0 })}
                          value={entityTypeFor(cal.id)}
                          onChange={(e) => void changeEntityType(cal.id, e.target.value as CalendarEntityType)}
                        >
                          <option value="professional">Profesional</option>
                          <option value="service">Servicio</option>
                          <option value="custom">Otra cosa...</option>
                        </select>

                        {entityTypeFor(cal.id) === "professional" && (
                          <select
                            className={css({ px: "2", py: "1.5", rounded: "md", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "xs", color: "#334155", bg: "white", flexShrink: 0 })}
                            value={linkedStaff?.id ?? ""}
                            onChange={(e) => {
                              const newStaffId = e.target.value || null;
                              // Desvincula el staff anterior si estaba vinculado a esta agenda
                              if (linkedStaff) {
                                void linkStaffCalendar(linkedStaff.id, null);
                              }
                              if (newStaffId) {
                                void linkStaffCalendar(newStaffId, cal.id);
                              }
                            }}
                          >
                            <option value="">Sin profesional</option>
                            {staff.map((s) => (
                              <option key={s.id} value={s.id} disabled={!!s.googleCalendarId && s.googleCalendarId !== cal.id}>
                                {s.name}
                                {s.googleCalendarId && s.googleCalendarId !== cal.id ? " (vinculado a otra)" : ""}
                              </option>
                            ))}
                          </select>
                        )}

                        {entityTypeFor(cal.id) === "service" && (
                          <select
                            className={css({ px: "2", py: "1.5", rounded: "md", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "xs", color: "#334155", bg: "white", flexShrink: 0 })}
                            value={calendarMappings[cal.id]?.serviceId ?? ""}
                            onChange={(e) => void saveCalendarMapping(cal.id, "service", { serviceId: e.target.value })}
                          >
                            {services.length === 0 && <option value="">Sin servicios cargados</option>}
                            {services.map((s) => {
                              const usedElsewhere = Object.entries(calendarMappings).some(
                                ([cid, m]) => cid !== cal.id && m.entityType === "service" && m.serviceId === s.id,
                              );
                              return (
                                <option key={s.id} value={s.id} disabled={usedElsewhere}>
                                  {s.name}
                                  {usedElsewhere ? " (vinculado a otra)" : ""}
                                </option>
                              );
                            })}
                          </select>
                        )}

                        {entityTypeFor(cal.id) === "custom" && (
                          <input
                            type="text"
                            placeholder="Ej: Sucursal Norte"
                            maxLength={120}
                            className={css({ px: "2", py: "1.5", rounded: "md", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "xs", color: "#334155", bg: "white", flexShrink: 0, minW: "0", flex: "1 1 160px" })}
                            value={customLabelDrafts[cal.id] ?? calendarMappings[cal.id]?.customLabel ?? ""}
                            onChange={(e) => setCustomLabelDrafts((prev) => ({ ...prev, [cal.id]: e.target.value }))}
                            onBlur={(e) => {
                              const trimmed = e.target.value.trim();
                              if (trimmed && trimmed !== calendarMappings[cal.id]?.customLabel) {
                                void saveCalendarMapping(cal.id, "custom", { customLabel: trimmed });
                              }
                            }}
                          />
                        )}

                        {/* Checkbox importar ocupación */}
                        <label className={css({ display: "flex", alignItems: "center", gap: "1.5", fontSize: "xs", color: "#334155", cursor: "pointer", flexShrink: 0 })}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => void toggleSelectedCalendar(cal.id, e.target.checked)}
                          />
                          Importar ocupación
                        </label>
                      </div>

                      {tzMismatch && (
                        <p className={css({ fontSize: "xs", color: "#92400e", bg: "#fffbeb", borderWidth: "1px", borderColor: "#fcd34d", rounded: "md", px: "2", py: "1.5" })}>
                          ⚠ Esta agenda de Google está en <strong>{cal.timeZone}</strong> ({formatUtcOffset(cal.utcOffsetMinutes!)}), un huso horario real distinto al de tu cuenta SFTurnos (
                          {formatUtcOffset(professionalUtcOffsetMinutes!)}) — {Math.abs((cal.utcOffsetMinutes! - professionalUtcOffsetMinutes!) / 60)}h de diferencia real.{" "}
                          Corregilo manualmente desde Google Calendar → Configuración de esta agenda → Huso horario.
                        </p>
                      )}
                      </div>
                    );
                  })}
                  {staff.length === 0 && (
                    <div className={css({ mt: "2", p: "3", rounded: "lg", bg: "#eef2ff", borderWidth: "1px", borderColor: "#c7d2fe" })}>
                      <p className={css({ fontSize: "xs", color: "#3730a3", mb: "2", lineHeight: "1.5" })}>
                        Detectamos {googleCalendars.length} agenda{googleCalendars.length !== 1 ? "s" : ""} en Google. Importalas como profesionales para asignar turnos y sincronizar a la agenda correcta.
                      </p>
                      <button
                        type="button"
                        disabled={calendarsLoading}
                        onClick={() => void importStaff()}
                        className={css({
                          px: "3",
                          py: "1.5",
                          rounded: "md",
                          fontSize: "xs",
                          fontWeight: "semibold",
                          color: "white",
                          bg: "#4f46e5",
                          borderWidth: "0",
                          cursor: calendarsLoading ? "wait" : "pointer",
                          opacity: calendarsLoading ? 0.7 : 1,
                        })}
                      >
                        {calendarsLoading ? "Importando…" : "Importar profesionales desde Google"}
                      </button>
                    </div>
                  )}
                  {staff.length > 0 && (
                    <p className={css({ fontSize: "xs", color: "#16a34a", mt: "1" })}>
                      ✓ {staff.filter((s) => s.googleCalendarId).length} profesional{staff.filter((s) => s.googleCalendarId).length !== 1 ? "es" : ""} vinculado{staff.filter((s) => s.googleCalendarId).length !== 1 ? "s" : ""} con Google
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </DashboardPageShell>
  );
}
